import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

const FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Available', value: 'available' },
  { label: 'Sold Out', value: 'soldOut' },
  { label: 'Black', value: 'blackInk' },
  { label: 'Color', value: 'colorInk' },
];

function FilterPopup({ currentFilters, onFilterChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(currentFilters);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleFilterToggle = (filterName) => {
    const newFilters = (prev) => {
      // If toggling 'All', reset everything else
      if (filterName === 'all') {
        const allFilters = {
          all: !prev.all,
          available: false,
          soldOut: false,
          blackInk: false,
          colorInk: false
        };
        onFilterChange(allFilters);
        return allFilters;
      }

      // If selecting any other filter, turn off 'all'
      const updatedFilters = {
        ...prev,
        all: false,
        [filterName]: !prev[filterName]
      };

      // If no filters are selected, default back to 'all'
      const hasActiveFilters = Object.entries(updatedFilters)
        .some(([key, value]) => key !== 'all' && value === true);

      if (!hasActiveFilters) {
        const defaultFilters = {
          all: true,
          available: false,
          soldOut: false,
          blackInk: false,
          colorInk: false
        };
        onFilterChange(defaultFilters);
        return defaultFilters;
      }

      onFilterChange(updatedFilters);
      return updatedFilters;
    };

    setTempFilters(newFilters);
  };

  const handleApply = () => {
    setIsOpen(false);
  };

  const handleClear = () => {
    const clearedFilters = {
      all: true,
      available: false,
      soldOut: false,
      blackInk: false,
      colorInk: false
    };
    setTempFilters(clearedFilters);
    onFilterChange(clearedFilters);
    setIsOpen(false);
  };

  const getActiveFiltersCount = () => {
    // Don't count 'all' in the active filters count
    const { all, ...otherFilters } = currentFilters;
    return Object.values(otherFilters).filter(Boolean).length;
  };

  const renderMobilePopup = () => {
    if (!isOpen || !isMobile) return null;

    const popup = (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-end justify-center" onClick={() => setIsOpen(false)}>
        <div 
          className="w-full bg-white rounded-t-2xl shadow-2xl p-4 transform translate-y-0" 
          onClick={e => e.stopPropagation()}
          style={{ maxHeight: '80vh', overflowY: 'auto' }}
        >
          <div className="w-full max-w-sm mx-auto">
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4"></div>
            <h3 className="text-lg font-medium mb-4">Filter By</h3>
            <div className="space-y-2">
              {FILTER_OPTIONS.map((option) => (
                <label key={option.value} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                  <input
                    type="checkbox"
                    checked={tempFilters[option.value]}
                    onChange={() => handleFilterToggle(option.value)}
                    className="w-4 h-4"
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
            <div className="mt-4 flex justify-between items-center">
              <button
                onClick={handleClear}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Clear
              </button>
              <button
                onClick={handleApply}
                className="px-4 py-2 bg-black hover:bg-gray-800 rounded-md text-white"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    );

    return ReactDOM.createPortal(popup, document.body);
  };

  const renderDesktopDropdown = () => {
    if (!isOpen || isMobile) return null;

    return (
      <div 
        ref={dropdownRef}
        className="absolute top-full left-0 mt-1 bg-white rounded-md shadow-lg p-4 min-w-[250px] z-50"
      >
        <div className="space-y-2">
          {FILTER_OPTIONS.map((option) => (
            <label key={option.value} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
              <input
                type="checkbox"
                checked={tempFilters[option.value]}
                onChange={() => handleFilterToggle(option.value)}
                className="w-4 h-4"
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
        <div className="mt-4 flex justify-between items-center">
          <button
            onClick={handleClear}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Clear
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-2 bg-black hover:bg-gray-800 rounded-md text-white"
          >
            Apply
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 flex items-center gap-2 active:bg-transparent focus:bg-transparent ring-0 focus:ring-0 active:ring-0 focus:outline-none active:outline-none"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        Filter
        {getActiveFiltersCount() > 0 ? (
          <span className="bg-gray-300 text-gray-700 rounded-full px-2 py-0.5 text-sm">
            {getActiveFiltersCount()}
          </span>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>
      {renderDesktopDropdown()}
      {renderMobilePopup()}
    </div>
  );
}

export default FilterPopup; 